import type { Message } from '@/types/api';
import type { UploadedAttachment } from '@/lib/uploader';

/**
 * Pure helpers for the Composer's send path, extracted so the send-guard,
 * ready-attachment collection, optimistic-bubble construction, and request
 * payload shaping can be unit-tested without rendering the (1400-line)
 * Composer or mocking the data store. The component keeps the React state +
 * side effects (optimistic upsert, api call, swap/rollback); these functions
 * only transform data.
 */

/** Minimal upload-slot shape the send path inspects (the full UploadSlot in
 *  the Composer carries an AbortController + progress fields we don't need). */
export interface ReadyCheckSlot {
  status: string;
  completed?: UploadedAttachment;
}

/** Completed attachments, in order, ready to attach to the outgoing message. */
export function collectReadyAttachments(slots: ReadyCheckSlot[]): UploadedAttachment[] {
  return slots.filter((u) => u.status === 'done' && u.completed).map((u) => u.completed!);
}

/** True while any upload is still preparing/uploading — blocks send so we
 *  never drop a half-uploaded attachment. (`error` slots don't block; the
 *  user can still send the text + the ones that succeeded.) */
export function hasUploadsInFlight(slots: ReadyCheckSlot[]): boolean {
  return slots.some((u) => u.status !== 'done' && u.status !== 'error');
}

/**
 * Whether a send should proceed: no upload in flight and there's actually
 * something to send (non-empty text OR ≥1 ready attachment). A previous send
 * still in flight does NOT block — sends are queued (see composerSend.ts),
 * so a quick second Enter is never silently dropped.
 */
export function canSendMessage(args: {
  content: string;
  readyCount: number;
  inFlight: boolean;
}): boolean {
  if (args.inFlight) return false;
  return Boolean(args.content) || args.readyCount > 0;
}

export interface OptimisticAuthor {
  id: string;
  platformId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

/**
 * Build the temporary message painted locally before the server roundtrip.
 * `id` carries the caller's `tmp-` prefix so the renderer styles it pending,
 * and empty content collapses to a single space to match the server contract
 * (a message must have text OR attachments; ' ' stands in for attachment-only).
 */
export function buildOptimisticMessage(args: {
  tmpId: string;
  channelId: string;
  content: string;
  attachments: UploadedAttachment[];
  replyingTo: Message | null;
  author: OptimisticAuthor;
  now?: Date;
}): Message {
  const { replyingTo } = args;
  return {
    id: args.tmpId,
    channelId: args.channelId,
    authorId: args.author.id,
    content: args.content || ' ',
    attachments: args.attachments.length > 0 ? args.attachments : null,
    mentions: [],
    replyToId: replyingTo?.id ?? null,
    replyTo: replyingTo
      ? {
          id: replyingTo.id,
          content: replyingTo.content,
          author: replyingTo.author,
          deletedAt: replyingTo.deletedAt,
        }
      : null,
    pinned: false,
    editedAt: null,
    deletedAt: null,
    createdAt: (args.now ?? new Date()).toISOString(),
    author: { ...args.author },
    reactions: [],
  };
}

export interface SendBatch<A> {
  /** Caption for this batch ('' on the trailing batches of an ungrouped send). */
  content: string;
  /** Attachments riding this batch. */
  atts: A[];
  /** Whether the reply-target rides this batch (only the first, ungrouped). */
  withReply: boolean;
}

/**
 * Split a send into message batches.
 *   - grouped (default) OR ≤1 attachment → a single batch carrying all
 *     attachments + the caption + the reply.
 *   - ungrouped with ≥2 attachments → one batch per attachment, so each lands
 *     as its own message; the caption and reply ride ONLY the first batch so
 *     the timeline isn't spammed with the repeated text / reply chip.
 * Generic over the attachment type to stay decoupled from UploadedAttachment.
 */
export function planSendBatches<A>(args: {
  content: string;
  ready: A[];
  grouped: boolean;
}): SendBatch<A>[] {
  const { content, ready, grouped } = args;
  if (grouped || ready.length <= 1) {
    return [{ content, atts: ready, withReply: true }];
  }
  return ready.map((a, i) => ({
    content: i === 0 ? content : '',
    atts: [a],
    withReply: i === 0,
  }));
}

export interface SendMessagePayload {
  content: string;
  uploadIds?: string[];
  replyToId?: string;
}

/** Shape the POST body: collapse empty content to ' ', and only include
 *  `uploadIds` / `replyToId` when present (the schema rejects empty arrays /
 *  null ids on these optional fields). */
export function buildSendPayload(args: {
  content: string;
  uploadIds: string[];
  replyToId: string | null;
}): SendMessagePayload {
  return {
    content: args.content || ' ',
    ...(args.uploadIds.length > 0 ? { uploadIds: args.uploadIds } : {}),
    ...(args.replyToId ? { replyToId: args.replyToId } : {}),
  };
}
